import React, { useState, useRef, useEffect } from 'react';
import './VideoPlayer.css';

function VideoPlayer({ video, onClose }) {
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showControls, setShowControls] = useState(true);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    const videoRef = useRef(null);
    const playerRef = useRef(null);
    const controlsTimeoutRef = useRef(null);

    useEffect(() => {
        const videoElement = videoRef.current;
        if (!videoElement) return;

        const handleLoadStart = () => setLoading(true);
        const handleCanPlay = () => setLoading(false);
        const handleError = (e) => {
            setLoading(false);
            setError('Failed to load video. Please try again.');
            console.error('Video error:', e);
        };
        
        const handleTimeUpdate = () => {
            setCurrentTime(videoElement.currentTime);
        };
        
        const handleDurationChange = () => {
            setDuration(videoElement.duration);
        };
        
        const handlePlay = () => setIsPlaying(true);
        const handlePause = () => setIsPlaying(false);
        
        const handleVolumeChange = () => {
            setVolume(videoElement.volume);
        };

        videoElement.addEventListener('loadstart', handleLoadStart);
        videoElement.addEventListener('canplay', handleCanPlay);
        videoElement.addEventListener('error', handleError);
        videoElement.addEventListener('timeupdate', handleTimeUpdate);
        videoElement.addEventListener('durationchange', handleDurationChange);
        videoElement.addEventListener('play', handlePlay);
        videoElement.addEventListener('pause', handlePause);
        videoElement.addEventListener('volumechange', handleVolumeChange);

        return () => {
            videoElement.removeEventListener('loadstart', handleLoadStart);
            videoElement.removeEventListener('canplay', handleCanPlay);
            videoElement.removeEventListener('error', handleError);
            videoElement.removeEventListener('timeupdate', handleTimeUpdate);
            videoElement.removeEventListener('durationchange', handleDurationChange);
            videoElement.removeEventListener('play', handlePlay);
            videoElement.removeEventListener('pause', handlePause);
            videoElement.removeEventListener('volumechange', handleVolumeChange);
        };
    }, []);

    const togglePlay = () => {
        const videoElement = videoRef.current;
        if (!videoElement) return;

        if (isPlaying) {
            videoElement.pause();
        } else {
            videoElement.play().catch(console.error);
        }
    };

    const handleSeek = (e) => {
        const videoElement = videoRef.current;
        if (!videoElement) return;

        const rect = e.currentTarget.getBoundingClientRect();
        const percentage = (e.clientX - rect.left) / rect.width;
        const newTime = percentage * duration;
        
        videoElement.currentTime = newTime;
        setCurrentTime(newTime);
    };

    const handleVolumeChange = (e) => {
        const newVolume = parseFloat(e.target.value);
        const videoElement = videoRef.current;
        if (!videoElement) return;

        videoElement.volume = newVolume;
        setVolume(newVolume);
    };

    const toggleFullscreen = () => {
        const playerElement = playerRef.current;
        if (!playerElement) return;

        if (!document.fullscreenElement) {
            playerElement.requestFullscreen().then(() => {
                setIsFullscreen(true);
            }).catch(console.error);
        } else {
            document.exitFullscreen().then(() => {
                setIsFullscreen(false);
            }).catch(console.error);
        }
    };

    const formatTime = (time) => {
        if (isNaN(time)) return '0:00';
        
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const hideControlsAfterDelay = () => {
        if (controlsTimeoutRef.current) {
            clearTimeout(controlsTimeoutRef.current);
        }
        
        controlsTimeoutRef.current = setTimeout(() => {
            if (isPlaying) {
                setShowControls(false);
            }
        }, 3000);
    };

    const showControlsTemporarily = () => {
        setShowControls(true);
        hideControlsAfterDelay();
    };

    const getVideoSource = () => {
        if (video.videoSource === 'upload') {
            return video.videoUrl;
        }
        
        // Handle YouTube URLs
        if (video.videoUrl.includes('youtube.com') || video.videoUrl.includes('youtu.be')) {
            return renderYouTubeEmbed();
        }
        
        // Handle Vimeo URLs
        if (video.videoUrl.includes('vimeo.com')) {
            return renderVimeoEmbed();
        }
        
        // Direct video URLs
        return video.videoUrl;
    };

    const renderYouTubeEmbed = () => {
        const getYouTubeId = (url) => {
            const regex = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/;
            const match = url.match(regex);
            return match ? match[1] : null;
        };

        const videoId = getYouTubeId(video.videoUrl);
        if (!videoId) return null;

        return (
            <iframe
                ref={videoRef}
                src={`https://www.youtube.com/embed/${videoId}?autoplay=0&controls=1&rel=0`}
                title={video.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="video-iframe"
            />
        );
    };

    const renderVimeoEmbed = () => {
        const getVimeoId = (url) => {
            const regex = /vimeo\.com\/([0-9]+)/;
            const match = url.match(regex);
            return match ? match[1] : null;
        };

        const videoId = getVimeoId(video.videoUrl);
        if (!videoId) return null;

        return (
            <iframe
                ref={videoRef}
                src={`https://player.vimeo.com/video/${videoId}?autoplay=0&controls=1`}
                title={video.title}
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                className="video-iframe"
            />
        );
    };

    const isEmbeddedVideo = video.videoUrl.includes('youtube.com') || 
                           video.videoUrl.includes('youtu.be') || 
                           video.videoUrl.includes('vimeo.com');

    return (
        <div className="video-player-overlay" onClick={onClose}>
            <div className="video-player-container" ref={playerRef} onClick={(e) => e.stopPropagation()}>
                <div className="video-player-header">
                    <h3>{video.title}</h3>
                    <button className="close-btn" onClick={onClose}>✕</button>
                </div>

                <div 
                    className="video-wrapper"
                    onMouseMove={showControlsTemporarily}
                    onMouseLeave={() => isPlaying && setShowControls(false)}
                >
                    {loading && (
                        <div className="video-loading">
                            <div className="video-player-loading-spinner"></div>
                            <p>Loading video...</p>
                        </div>
                    )}

                    {error && (
                        <div className="video-error">
                            <p>{error}</p>
                            <button onClick={() => window.location.reload()}>Retry</button>
                        </div>
                    )}

                    {isEmbeddedVideo ? (
                        getVideoSource()
                    ) : (
                        <>
                            <video
                                ref={videoRef}
                                src={getVideoSource()}
                                poster={video.thumbnailUrl}
                                preload="metadata"
                                className="video-element"
                                onClick={togglePlay}
                            />

                            {/* Custom Controls for uploaded videos */}
                            <div className={`video-controls ${showControls ? 'visible' : ''}`}>
                                <div className="progress-bar" onClick={handleSeek}>
                                    <div 
                                        className="progress-filled"
                                        style={{ width: `${(currentTime / duration) * 100 || 0}%` }}
                                    />
                                </div>

                                <div className="controls-row">
                                    <button className="control-btn" onClick={togglePlay}>
                                        {isPlaying ? '⏸️' : '▶️'}
                                    </button>

                                    <div className="time-display">
                                        {formatTime(currentTime)} / {formatTime(duration)}
                                    </div>

                                    <div className="volume-control">
                                        <span>🔊</span>
                                        <input
                                            type="range"
                                            min="0"
                                            max="1"
                                            step="0.1"
                                            value={volume}
                                            onChange={handleVolumeChange}
                                            className="volume-slider"
                                        />
                                    </div>

                                    <button className="control-btn" onClick={toggleFullscreen}>
                                        {isFullscreen ? '⏹️' : '⛶'}
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {video.description && (
                    <div className="video-description">
                        <p>{video.description}</p>
                    </div>
                )}

                {video.videoMetadata && (
                    <div className="video-metadata">
                        <small>
                            {video.videoMetadata.width && video.videoMetadata.height && (
                                <span>Resolution: {video.videoMetadata.width}x{video.videoMetadata.height} • </span>
                            )}
                            {video.videoMetadata.duration > 0 && (
                                <span>Duration: {formatTime(video.videoMetadata.duration)} • </span>
                            )}
                            Size: {(video.videoMetadata.fileSize / (1024 * 1024)).toFixed(1)}MB
                        </small>
                    </div>
                )}
            </div>
        </div>
    );
}

export default VideoPlayer;
